import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Loader2, X } from 'lucide-react'

// ======================================================================
// PRIMITIVES
// ======================================================================
//
// ICONS ARE lucide-react STROKE ICONS AND NOTHING ELSE. No emoji is used
// as an interface icon anywhere in this application. An emoji is a font
// glyph: it renders differently on every platform, cannot inherit the
// accent colour, has no accessible name, and on a disciplinary case
// screen it carries a tone nobody chose. Where a glyph appears in
// content — somebody typed one into a working note — it is content and
// is rendered as typed.

export function Panel({ title, eyebrow, right, children, className = '', tone }) {
  const toneClass =
    tone === 'warn' ? 'clock-warn' : tone === 'breach' ? 'clock-breach' : ''
  return (
    <section className={`panel anim-rise ${toneClass} ${className}`}>
      {(title || eyebrow || right) && (
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-3.5">
          <div className="min-w-0">
            {eyebrow && <div className="eyebrow mb-0.5">{eyebrow}</div>}
            {title && (
              <h2 className="font-head text-[15px] font-semibold leading-tight">
                {title}
              </h2>
            )}
          </div>
          {right && <div className="flex shrink-0 items-center gap-2">{right}</div>}
        </header>
      )}
      <div className="px-5 py-4">{children}</div>
    </section>
  )
}

export function Chip({ tone, children, title }) {
  const cls =
    tone === 'accent'
      ? 'chip chip-accent'
      : tone === 'amber'
        ? 'chip chip-amber'
        : tone === 'danger'
          ? 'chip chip-danger'
          : tone === 'blue'
            ? 'chip chip-blue'
            : 'chip'
  return (
    <span className={cls} title={title}>
      {children}
    </span>
  )
}

export function Button({
  variant = 'default',
  size,
  busy,
  icon: Icon,
  children,
  className = '',
  ...rest
}) {
  const variantClass =
    variant === 'primary'
      ? 'btn btn-primary'
      : variant === 'ghost'
        ? 'btn btn-ghost'
        : variant === 'danger'
          ? 'btn btn-danger'
          : 'btn'
  return (
    <button
      type="button"
      className={`${variantClass} ${size === 'sm' ? 'btn-sm' : ''} ${className}`}
      disabled={busy || rest.disabled}
      {...rest}
    >
      {busy ? (
        <Loader2 size={14} className="animate-spin" strokeWidth={2} />
      ) : (
        Icon && <Icon size={size === 'sm' ? 13 : 14} strokeWidth={1.9} />
      )}
      {children}
    </button>
  )
}

export function Field({ label, hint, children, required }) {
  return (
    <label className="block">
      {label && (
        <div className="mb-1.5 flex items-baseline gap-2">
          <span className="font-head text-[12px] font-semibold text-ink">{label}</span>
          {required && <span className="text-[10.5px] text-accent">required</span>}
        </div>
      )}
      {children}
      {hint && <p className="mt-1.5 text-[11.5px] leading-relaxed text-faint">{hint}</p>}
    </label>
  )
}

/** THE REASON FIELD.
 *
 *  er_cases/service.py refuses every stage change, pause, resume,
 *  target change and closure without one, and the text is shown verbatim
 *  to every party with view_case — including the subject of the case.
 *  It is therefore the primary control on these forms, not a box below
 *  the button, and it says who will read it. */
export function ReasonField({
  value,
  onChange,
  label = 'Reason',
  hint = 'Recorded against the case and shown, word for word, to everyone who can see it — including the person the case is about.',
  rows = 3,
  autoFocus,
}) {
  return (
    <Field label={label} hint={hint} required>
      <textarea
        className="field field-reason"
        rows={rows}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  )
}

/** An intentional empty state.
 *
 *  "Nothing here" and "we could not load it" must never look the same,
 *  and neither may hint that something exists which is being withheld.
 *  This component states an absence and stops. */
export function Empty({ icon: Icon, title, children }) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-9 text-center">
      {Icon && <Icon size={22} strokeWidth={1.4} className="text-faint" />}
      <p className="font-head text-[13.5px] font-medium text-muted">{title}</p>
      {children && (
        <p className="max-w-md text-[12.5px] leading-relaxed text-faint">{children}</p>
      )}
    </div>
  )
}

export function Spinner({ label = 'Loading' }) {
  return (
    <div className="flex items-center gap-2 px-1 py-6 text-faint">
      <Loader2 size={15} className="animate-spin" strokeWidth={2} />
      <span className="text-[12.5px]">{label}…</span>
    </div>
  )
}

/** A refusal from the backend, shown as the backend worded it.
 *
 *  THE MESSAGE IS NEVER REWRITTEN AND NEVER GUESSED AT. er_cases refuses
 *  with a `detail` string written for the person who will read it —
 *  "You cannot grant yourself a role on a case you administer", "A
 *  recording needs a record of who consented to it" — and the reason
 *  CODE stays server-side in the audit chain. Substituting our own
 *  wording would put a sentence in front of the user that nobody wrote
 *  and that may not be true. */
export function Refusal({ error, onDismiss }) {
  if (!error) return null
  const status = error.status
  const text =
    status === 0
      ? 'The cases service could not be reached.'
      : status === 429
        ? 'Too many requests in a short period. Wait a minute and try again.'
        : status === 503
          ? 'Cases is temporarily unavailable.'
          : error.message
  return (
    <div
      role="alert"
      className="anim-fade mb-3 flex items-start gap-2.5 rounded-md border border-danger-line bg-danger-dim px-3.5 py-2.5"
    >
      <AlertTriangle size={15} strokeWidth={1.9} className="mt-0.5 shrink-0 text-danger" />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] leading-relaxed text-ink">{text}</p>
        {status > 0 && status !== 429 && status !== 503 && (
          <p className="mt-0.5 text-[10.5px] text-faint">Refused ({status})</p>
        )}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="btn-ghost shrink-0 rounded p-0.5 text-faint hover:text-ink"
          aria-label="Dismiss"
        >
          <X size={14} strokeWidth={2} />
        </button>
      )}
    </div>
  )
}

/** A statement of fact the user must read before continuing. Used for
 *  the working-notes warning and the disclosure-clearing warning. */
export function Notice({ tone = 'amber', icon: Icon = AlertTriangle, title, children }) {
  // `danger` added for the workflow catalogue's coverage_kind (073):
  // a definition documenting behaviour that DOES NOT EXIST is not the
  // same warning as one whose wording is imprecise, and drawing them in
  // the same colour is how a reader learns to ignore both. Additive —
  // the amber and accent tones are untouched, so every existing caller
  // renders exactly as before.
  const border =
    tone === 'danger' ? 'border-danger-line'
      : tone === 'amber' ? 'border-amber-line' : 'border-accent-line'
  const bg =
    tone === 'danger' ? 'bg-danger-dim'
      : tone === 'amber' ? 'bg-amber-dim' : 'bg-accent-dim'
  const fg =
    tone === 'danger' ? 'text-danger'
      : tone === 'amber' ? 'text-amber' : 'text-accent'
  return (
    <div className={`flex items-start gap-3 rounded-md border ${border} ${bg} px-4 py-3`}>
      <Icon size={16} strokeWidth={1.9} className={`mt-0.5 shrink-0 ${fg}`} />
      <div className="min-w-0">
        {title && (
          <p className={`font-head text-[12.5px] font-semibold ${fg}`}>{title}</p>
        )}
        {/* MEASURED (task 26). A Notice is prose — the working-papers
            warning, the disclosure-clearing warning, the "what this list
            is" banner — and on a full-width page its body would
            otherwise run the entire window. .measure caps it at a
            reading width and binds on nothing when the panel is
            narrower. */}
        <div className="measure text-[12.5px] leading-relaxed text-muted">{children}</div>
      </div>
    </div>
  )
}

export function Modal({ open, onClose, title, subtitle, children, width = 'max-w-lg' }) {
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    ref.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  if (!open) return null
  return (
    <div
      className="anim-fade fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:p-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`anim-rise panel w-full ${width} my-auto outline-none`}
        style={{ boxShadow: 'var(--shadow-modal)' }}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="font-head text-[15px] font-semibold">{title}</h2>
            {subtitle && (
              <p className="mt-0.5 text-[12px] leading-relaxed text-muted">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost shrink-0 p-1.5"
            aria-label="Close"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </header>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  )
}

/** A progress bar with a real number behind it. Used for uploads and
 *  downloads, both of which can run for minutes on a 250 MB recording. */
export function Progress({ value, label }) {
  const pct = Math.round((value || 0) * 100)
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-[11.5px]">
        <span className="text-muted">{label}</span>
        <span className="mono text-accent">{pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-3">
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-150"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

/** A two-step confirm for a write whose consequence is not obvious from
 *  the button — editing a disclosed allegation, revoking a party. */
export function useConfirm() {
  const [pending, setPending] = useState(null)
  return {
    pending,
    ask: (config) => setPending(config),
    dismiss: () => setPending(null),
  }
}

export function KeyValue({ items }) {
  const rows = items.filter((i) => i && i[1] != null && i[1] !== '')
  if (!rows.length) return null
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5 text-[12.5px]">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="whitespace-nowrap text-faint">{k}</dt>
          <dd className="min-w-0 break-words text-ink">{v}</dd>
        </div>
      ))}
    </dl>
  )
}
